import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { 
  faFlask,
  faHeadSideMask,
  faVirus,
  faDna,
  faPersonWalkingArrowRight,
  faUsers,
  faUserDoctor,
  faHospitalUser,
  faTruckFast,
  faSquarePollVertical,
  faPeopleArrows,
  faListCheck
} from '@fortawesome/free-solid-svg-icons';
import './Home.css';

// Add icons to library
library.add(faFlask, faHeadSideMask, faVirus, faDna, faPersonWalkingArrowRight, faUsers, faUserDoctor, faHospitalUser, faTruckFast, faSquarePollVertical, faPeopleArrows, faListCheck);

// Helper function to match the byPrefixAndName format
const byPrefixAndName = {
  fas: (iconName) => {
    const iconMap = {
      'flask-vial': faFlask,
      'mask-face': faHeadSideMask,
      'virus-covid': faVirus,
      'dna': faDna,
      'person-walking-arrow-right': faPersonWalkingArrowRight,
      'users': faUsers,
      'user-doctor': faUserDoctor,
      'hospital-user': faHospitalUser,
      'truck-fast': faTruckFast,
      'square-poll-vertical': faSquarePollVertical,
      'people-arrows': faPeopleArrows,
      'list-check': faListCheck
    };
    return iconMap[iconName];
  }
};

const Home = () => {
  const bannerHeadingRef = useRef(null);

  // Check if banner image exists, otherwise use solid color
  const bannerStyle = {
    backgroundImage: 'url(/banner.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  useEffect(() => {
    const adjustFontSize = () => {
      if (!bannerHeadingRef.current) return;
      
      const element = bannerHeadingRef.current;
      const container = element.parentElement;
      const maxWidth = container.offsetWidth - 40; // Account for padding
      
      // Get current computed style to use existing line-height
      const computedStyle = window.getComputedStyle(element);
      const currentFontSize = parseFloat(computedStyle.fontSize);
      const lineHeight = parseFloat(computedStyle.lineHeight) / currentFontSize;
      
      // Binary search for optimal font size
      let minSize = 16;
      // Limit max size on mobile devices
      const isMobile = window.innerWidth <= 768;
      const maxSizeMultiplier = isMobile ? 12 : 8;
      let maxSize = Math.min(maxWidth / maxSizeMultiplier, window.innerWidth / maxSizeMultiplier);
      let optimalSize = Math.min(maxSize, currentFontSize * 1.2); // Start closer to current size
      
      // Find the largest font size that fits in 2 lines
      for (let i = 0; i < 15; i++) { // Reduced iterations for faster execution
        const testSize = (minSize + maxSize) / 2;
        element.style.fontSize = `${testSize}px`;
        
        // Wait for reflow
        void element.offsetHeight;
        
        const height = element.scrollHeight;
        const twoLineHeight = testSize * lineHeight * 2.1; // Allow small margin
        
        if (height <= twoLineHeight) {
          optimalSize = testSize;
          minSize = testSize;
        } else {
          maxSize = testSize;
        }
      }
      
      element.style.fontSize = `${optimalSize}px`;
    };

    // Run immediately and on resize
    adjustFontSize();
    window.addEventListener('resize', adjustFontSize);
    
    return () => {
      window.removeEventListener('resize', adjustFontSize);
    };
  }, []);

  return (
    <div className="home">
      <section className="phone-section">
        <div className="phone-container">
          <div className="phone-item">
            <span className="phone-label">Laboratory</span>
            <a href="tel:+18007963894" className="phone-number">1 (800) 796-3894</a>
          </div>
          <div className="phone-item">
            <span className="phone-label">Business Office</span>
            <a href="tel:+13186983250" className="phone-number">(318) 698-3250</a>
          </div>
          <div className="phone-item">
            <span className="phone-label">Billing</span>
            <a href="tel:+15125150065" className="phone-number">(512) 515-0065</a>
          </div>
          <div className="phone-item">
            <span className="phone-label">Stat Pickup</span>
            <a href="tel:+18887961449" className="phone-number">1 (888) 796-1449</a>
          </div>
        </div>
      </section>
      <section className="banner-section" style={bannerStyle}>
        <div className="banner-overlay">
          <div className="banner-content">
            <h1 ref={bannerHeadingRef}>Providing Your Long-Term Care Facility with Fast, Accurate, and Personal Laboratory Services since 1987</h1>
          </div>
        </div>
      </section>

      <section className="content-section">
        <div className="container">
          <h2 className="welcome-heading">Welcome to Doctors Lab</h2>

          <h2 className="services-heading">Our Services</h2>
          <div className="services-grid">
            <div className="service-card">
              <FontAwesomeIcon icon={byPrefixAndName.fas('flask-vial')} className="service-icon" />
              <h3>Routine Diagnostic Testing</h3>
              <p>
                Comprehensive routine diagnostic testing services to support your healthcare needs. 
                We provide accurate and timely results for a wide range of diagnostic tests.
              </p>
            </div>

            <div className="service-card">
              <FontAwesomeIcon icon={byPrefixAndName.fas('mask-face')} className="service-icon" />
              <h3>COVID-19 Testing</h3>
              <p>
                Reliable COVID-19 testing services to help keep your facility safe. 
                We offer rapid and PCR testing options to meet your specific requirements.
              </p>
            </div>

            <div className="service-card">
              <FontAwesomeIcon icon={byPrefixAndName.fas('virus-covid')} className="service-icon" />
              <h3>Respiratory Pathogen Testing</h3>
              <p>
                Advanced respiratory pathogen testing to identify and diagnose various 
                respiratory infections quickly and accurately.
              </p>
            </div>

            <div className="service-card">
              <FontAwesomeIcon icon={byPrefixAndName.fas('dna')} className="service-icon" />
              <h3>Molecular UTI-ID (PCR+)</h3>
              <p>
                State-of-the-art molecular testing for urinary tract infections using 
                PCR technology for precise identification and diagnosis.
              </p>
            </div>
          </div>

          <h2 className="services-heading">Who We Service</h2>
          <div className="who-we-service-grid">
            <div className="service-block">
              <FontAwesomeIcon icon={byPrefixAndName.fas('person-walking-arrow-right')} className="service-icon" />
              <h3>Walk-In Clients</h3>
              <p>
                DOCTORS LAB welcomes patients who choose to utilize our services. Anyone may utilize the laboratory of his or her choice for testing. For patients with Physicians Orders we require the Physicians Order, a photo identification and a current health insurance card. Medicare and Medicaid are also accepted.
              </p>
            </div>

            <div className="service-block">
              <FontAwesomeIcon icon={byPrefixAndName.fas('users')} className="service-icon" />
              <h3>Employers</h3>
              <p>
                DOCTORS LAB offers employers Drug Screening for Pre-Employment, Random Sampling and For-Cause testing. Urine Drug Screens and Breathalyzer (alcohol) testing is available. Specimen collection can be done at the workplace or our facility.
              </p>
            </div>

            <div className="service-block">
              <FontAwesomeIcon icon={byPrefixAndName.fas('user-doctor')} className="service-icon" />
              <h3>Clinics and Physician's Offices</h3>
              <p>
                DOCTORS LAB offers services to physicians via specimen pick-up and processing in our laboratory facility or patients can be directed to our location for routine, pre-op or other testing needs.
              </p>
            </div>

            <div className="service-block">
              <FontAwesomeIcon icon={byPrefixAndName.fas('hospital-user')} className="service-icon" />
              <h3>Long Term Care Facilities</h3>
              <p>
                DOCTORS LAB offers phlebotomy services for patients in Nursing Homes, Assisted Living Facilities, Hospice and Homebound. Our team of phlebotomists will draw specimens per Physician's Order for processing at our laboratory facility. Daily, on-demand and STAT service is available.
              </p>
            </div>
          </div>

          <div className="why-choose-section">
            <h2>Why Choose Doctors Lab?</h2>
            <div className="features-list">
              <div className="feature-item">
                <FontAwesomeIcon icon={byPrefixAndName.fas('truck-fast')} className="service-icon" />
                <h3>Fast Turnaround Times</h3>
                <p>We understand the importance of timely results in healthcare decisions.</p>
              </div>
              <div className="feature-item">
                <FontAwesomeIcon icon={byPrefixAndName.fas('square-poll-vertical')} className="service-icon" />
                <h3>Accurate Results</h3>
                <p>Our state-of-the-art equipment and experienced staff ensure reliable results.</p>
              </div>
              <div className="feature-item">
                <FontAwesomeIcon icon={byPrefixAndName.fas('people-arrows')} className="service-icon" />
                <h3>Personalized Service</h3>
                <p>We work closely with your facility to meet your specific laboratory needs.</p>
              </div>
              <div className="feature-item">
                <FontAwesomeIcon icon={byPrefixAndName.fas('list-check')} className="service-icon" />
                <h3>Full-Service Laboratory</h3>
                <p>Comprehensive testing services all in one convenient location.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;

